import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Signal,
  viewChild,
} from '@angular/core';
import {IPermanentOrdinance, IPrescriptionAttendanceDialog} from './prescription-attendance-dialog.interface';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {dateObjectToISOString, IControlObject} from 'frontier/nucleus';
import {combineLatest, Observable, Subscription, switchMap} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {CellValueChangedEvent, ColumnApi, GridApi} from 'ag-grid-community';
import {SelectionModel} from '@angular/cdk/collections';
import {DateTime} from 'luxon';
import {UploadWrapperComponent} from 'frontier/browserkit/src/lib/components/upload-wrapper/upload-wrapper.component';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {DialogHeaderComponent} from 'frontier/browserkit/src/lib/components/dialog-header/dialog-header.component';
import {
  ClientSideTableControlComponent,
  ConfirmIconButtonComponent,
  DateElementComponent,
  PdfPipe
} from 'frontier/browserkit';
import AppControlGuids from '../../../../../api/ControlGuids';

interface Form {
  selectedOrdinance: FormControl<string>;
  attendanceCertificate: FormControl<any>;
  fromDate: FormControl<string>;
  toDate: FormControl<string>;
  selectedCarts: FormControl<IControlObject[]>;
  hasDocument: FormControl<boolean>;
}

@Component({
  selector: 'app-prescription-attendance-dialog',
  templateUrl: './prescription-attendance-dialog.component.html',
  styleUrl: './prescription-attendance-dialog.component.scss',
  standalone: true,
  imports: [DialogHeaderComponent, MatDialogContent, FormsModule, ReactiveFormsModule, MatError, MatFormField, MatLabel,
    MatSelect, MatOption, MatSlideToggle, UploadWrapperComponent, DateElementComponent, ClientSideTableControlComponent,
    MatDialogActions, ConfirmIconButtonComponent, PdfPipe]
})
export class PrescriptionAttendanceDialogComponent implements OnInit, OnDestroy {
  tableRef: Signal<ClientSideTableControlComponent> = viewChild(ClientSideTableControlComponent);

  form: FormGroup<Form>;

  selectedCartsModel = new SelectionModel<string>(true);
  clientGUID = AppControlGuids.TCartList;

  gridApi: GridApi;
  colApi: ColumnApi;

  constructor(
    public dialogRef: MatDialogRef<PrescriptionAttendanceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Partial<IPrescriptionAttendanceDialog>,
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder) {
    this.form = fb.group<Form>({
      selectedOrdinance: fb.control(null, [Validators.required]),
      attendanceCertificate: fb.control(null, [Validators.required]),
      fromDate: fb.control(null, [Validators.required]),
      toDate: fb.control(null, [Validators.required]),
      selectedCarts: fb.control(null),
      hasDocument: fb.control(true)
    });
    this.form.patchValue({...data, selectedOrdinance: null, fromDate: null, toDate: null}, {emitEvent: false});
  }

  protected readonly oncancel = oncancel;

  private subs = new Subscription();
  tableInitialized$ = new EventEmitter();

  ngOnInit() {
    this.subs.add(
      this.selectedOrdinance().pipe(
        map(ordinance => {
          const fromDate = DateTime.fromISO(ordinance.startdate).toFormat('yyyy-MM-dd');
          const toDate = DateTime.fromISO(ordinance.enddate).toFormat('yyyy-MM-dd');
          this.form.patchValue({
            fromDate,
            toDate
          }, {emitEvent: true});
        })).subscribe()
    )

    this.subs.add(
      combineLatest([
        this.tableInitialized$,
        combineLatest(
          [
            this.form.controls.fromDate.valueChanges.pipe(
              map(v => v)),
            this.form.controls.toDate.valueChanges.pipe(
              map(v => v))
          ]
        ).pipe(
          map(([fromDate, toDate]) => {
            return {startdate: fromDate, enddate: toDate}
          })),
      ])
        .pipe(
          filter(() => this.form.controls.fromDate.valid && this.form.controls.toDate.valid),
          switchMap(([evt, dateFilter]) => {
            const signature = this.form.value.selectedOrdinance;

            return this.tableRef().setApiInstanceAndFetch({
              ...this.tableRef().apiInstance,
              filter: {
                ...this.tableRef().apiInstance.filter,
                parent: this.data.permanentOrdinances.find((so => so.signature === signature)),
                typename: 'PermanentOrdinance',
                ...{startdate: dateFilter.startdate, enddate: dateFilter.enddate}
              }
            })
          }),
          tap(() => {
            this.updateSelectionModel();
          })
        )
        .subscribe()
    )

    this.subs.add(
      this.form.controls.hasDocument.valueChanges.pipe(
        tap((b) => {
          if (b) {
            this.form.controls.attendanceCertificate.setValidators([Validators.required])
          } else {
            this.form.controls.attendanceCertificate.clearValidators()
          }
          this.form.controls.attendanceCertificate.updateValueAndValidity();
        })
      ).subscribe()
    )

    if (this.data.permanentOrdinances.length === 1) {
      this.form.patchValue({
        selectedOrdinance: this.data.permanentOrdinances[0].signature
      })
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  selectedOrdinance(): Observable<IPermanentOrdinance> {
    return this.form.controls.selectedOrdinance.valueChanges.pipe(
      map(signature => this.data.permanentOrdinances
        .find((so => so.signature === signature))))
  }

  onCancel() {
    this.dialogRef.close();
  }

  onConfirm() {
    const selectedCarts: IControlObject[] = [];
    this.tableRef().gridRef.api.forEachNode(
      node => {
        console.log(node);
        if (node.data?.[0]?.value === true) selectedCarts.push(node.data.apiRow.obj);
      }
    )
    const formValue = this.form.value;
    const dialogRes: IPrescriptionAttendanceDialog = {
      fromDate: dateObjectToISOString(formValue.fromDate),
      toDate: dateObjectToISOString(formValue.toDate),
      selectedCarts,
      selectedOrdinance: this.data.permanentOrdinances.find((so => so.signature === formValue.selectedOrdinance)),
      attendanceCertificate: formValue.hasDocument ? formValue.attendanceCertificate || null : null,
    }
    this.dialogRef.close(dialogRes);
  }

  updateSelectionModel() {
    this.selectedCartsModel.clear();
    const selectedRows = this.tableRef().data.rowData.filter(r => r[0].value === true);
    this.selectedCartsModel.select(...selectedRows.map(r => r.apiRow.obj.signature) as string[]);
  }

  onCellValueChange(event: CellValueChangedEvent<any>) {
    this.selectedCartsModel.toggle(event.data.apiRow.obj.signature);
  }
}
